/**
 * Recuperación cuando la app no puede descargar una pantalla (#64).
 *
 * Cada superficie y cada pantalla se baja aparte con lazy(). Si el servidor no responde o se
 * publicó una versión nueva (los archivos de la anterior ya no existen), la descarga falla. La
 * app recarga UNA vez para traer la versión vigente; si en esa misma pantalla vuelve a fallar,
 * pinta un aviso con un botón para recargar. La guarda es una marca en sessionStorage con la
 * pantalla donde se recargó: así nunca entra en un bucle de recargas.
 */

const MARCA = 'uniper:recarga-por-descarga'

/** Los mensajes con que cada navegador (y Vite) reporta una pantalla que no se pudo bajar. */
const DE_CARGA = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /unable to preload css/i, // Vite, cuando falla el CSS de la pantalla
]

export function esErrorDeCarga(error: unknown): boolean {
  const mensaje =
    error instanceof Error
      ? error.message
      : error && typeof error === 'object' && 'message' in error
        ? String((error as { message: unknown }).message)
        : typeof error === 'string'
          ? error
          : ''
  return DE_CARGA.some((r) => r.test(mensaje))
}

/** La pantalla actual, como la recuerda la marca. */
const rutaActual = () => window.location.pathname + window.location.search

/** Se aparta para que las pruebas puedan espiarlo: jsdom no sabe recargar. */
export const navegador = {
  recargar: () => window.location.reload(),
}

/**
 * Recarga si en esta pantalla todavía no se recargó por una descarga fallida. Devuelve si
 * recargó. Sin sessionStorage (modo privado estricto) no hay guarda: no recarga sola, para no
 * arriesgar un bucle, y queda el aviso con su botón.
 */
export function recargarUnaVez(ruta = rutaActual()): boolean {
  try {
    if (window.sessionStorage.getItem(MARCA) === ruta) return false
    window.sessionStorage.setItem(MARCA, ruta)
  } catch {
    return false
  }
  navegador.recargar()
  return true
}

/**
 * Una pantalla distinta abrió bien: la marca ya no aplica y, si más adelante vuelve a fallar
 * una descarga, se puede recargar otra vez. La de la pantalla recargada se conserva, que es la
 * que evita el bucle.
 */
export function cargaExitosa(ruta = rutaActual()) {
  try {
    const marca = window.sessionStorage.getItem(MARCA)
    if (marca !== null && marca !== ruta) window.sessionStorage.removeItem(MARCA)
  } catch {
    // Sin almacenamiento no hay marca que limpiar.
  }
}

/**
 * En producción Vite avisa con `vite:preloadError` antes de lanzar el error de descarga. Si se
 * recarga, se cancela el error para que no alcance a pintarse la pantalla de error.
 */
export function escucharErroresDeDescarga() {
  const alFallar = (e: Event) => {
    if (recargarUnaVez()) e.preventDefault()
  }
  window.addEventListener('vite:preloadError', alFallar)
  return () => window.removeEventListener('vite:preloadError', alFallar)
}
